// API Service for form submissions
// Connects to the Cloudflare Worker backend

interface FormSubmissionResponse {
  success: boolean;
  message: string;
  id: string;
  timestamp: string;
  data?: Record<string, unknown>;
  error?: string;
  errors?: Record<string, string>; // Field-specific validation errors
}

interface FormSubmissionError extends Error {
  fieldErrors?: Record<string, string>;
}

/**
 * Submit form data to the backend API
 * @param formData - The form data to submit
 * @returns Promise with submission response
 */
export const submitForm = async (formData: Record<string, unknown>): Promise<FormSubmissionResponse> => {
  try {
    // Determine API URL based on environment
    const apiUrl = import.meta.env.VITE_API_URL || '';
    const endpoint = `${apiUrl}/api/forms/submit`;
    
    // Make the API call
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    });
    
    // Parse the response
    const result = await response.json();
    
    // Handle non-success HTTP status codes
    if (!response.ok) {
      // If there are field-specific errors, throw them with the error
      if (result.errors) {
        const error = new Error(result.error || `Server-Fehler: ${response.status}`) as FormSubmissionError;
        error.fieldErrors = result.errors;
        throw error;
      }
      throw new Error(result.error || `Server-Fehler: ${response.status}`);
    }

    // Handle API-level errors
    if (!result.success) {
      // If there are field-specific errors, throw them with the error
      if (result.errors) {
        const error = new Error(result.error || 'Formular konnte nicht übermittelt werden.') as FormSubmissionError;
        error.fieldErrors = result.errors;
        throw error;
      }
      throw new Error(result.error || 'Formular konnte nicht übermittelt werden.');
    }
    
    return result;
    
  } catch (error) {
    // Network or fetch errors
    if (error instanceof Error && error.message === 'Failed to fetch') {
      throw new Error('Netzwerkfehler: Bitte überprüfen Sie Ihre Internetverbindung.');
    }
    
    // Re-throw the error for the form component to handle
    throw error;
  }
};

/**
 * Helper function to validate email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};